/**
 * Passage-destination info sheet. Opened by tapping the destination marker on the
 * chart. Shows where the destination is (and its range/bearing from the boat) and,
 * the reason this exists, gives a clear way to remove it: "Clear destination" wipes
 * it from the store, which drops the marker and any go-to line. Mirrors how the
 * other chart objects (clearing bearings/depths) offer a delete from their sheet.
 */
import { useChartPlotterStore } from '@/store';
import { useRoutingStore } from '@/store/routing';
import { useShellStore } from '@/store/shell';
import {
  formatPosition,
  formatBearing,
  formatDistance,
  rhumbBearingDeg,
  rhumbDistanceNm,
} from '@/nav';
import { haptic } from '@/ui/haptics';
import { setDestinationAndOfferRoute } from './setDestinationFlow';
import { Sheet } from './Sheet';

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="acp-kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

export function DestinationSheet() {
  const destination = useRoutingStore((s) => s.destination);
  const clearDestination = useRoutingStore((s) => s.clearDestination);
  const ownship = useChartPlotterStore((s) => s.ownship);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);

  if (!destination) return <Sheet name="destination" title="Destination" />;

  const hasBoat = !!ownship.position;
  const rng = hasBoat ? rhumbDistanceNm(ownship.position!, destination.position) : null;
  const brg = hasBoat ? rhumbBearingDeg(ownship.position!, destination.position) : null;

  function clear() {
    clearDestination();
    haptic('commit');
    closeSheet();
    showToast('Destination cleared');
  }

  function routeTo() {
    // Reuse the very flow the long-press "Set destination" ring uses: it re-sets
    // this same point as the destination (a no-op, it's already set) and then
    // offers Manual or Auto to plot the route. We close the sheet first so the
    // choice prompt isn't hidden behind it. With no fix there's no start to plot
    // from, which is why the button's disabled until we have one.
    haptic('commit');
    closeSheet();
    setDestinationAndOfferRoute(destination!.position, destination!.name ?? undefined);
  }

  return (
    <Sheet
      name="destination"
      title="Destination"
      sub="Where the passage is heading. Clear it to remove the marker and any go-to line."
    >
      <Row k="Name" v={destination.name ?? 'Destination'} />
      <Row k="Position" v={formatPosition(destination.position)} />
      <Row k="Range from boat" v={rng !== null ? formatDistance(rng) : 'No fix'} />
      <Row k="Bearing from boat" v={brg !== null ? formatBearing(brg) : 'No fix'} />

      <div className="acp-btn-row">
        <button
          type="button"
          className="acp-btn"
          onClick={routeTo}
          disabled={!hasBoat}
          title={hasBoat ? 'Plot a route to this destination' : 'No boat fix yet to plot a route from'}
        >
          Route to
        </button>
        <button type="button" className="acp-btn" onClick={clear}>
          Clear destination
        </button>
        <button type="button" className="acp-btn primary" onClick={() => closeSheet()}>
          Done
        </button>
      </div>
    </Sheet>
  );
}
